/* import __COLOCATED_TEMPLATE__ from './shopify.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import { use } from 'ember-resources/util/function-resource';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { get } from 'embercom/lib/ajax';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { SyncBehaviorType, SourceType } from 'embercom/models/import-service/import-source';

interface ShopifyShop {
  shopify_domain: string;
}

interface Signature {
  Args: {
    modal: any;
    title?: string;
    onNavigationBack?: () => void;
    onNavigationNext?: (viewId?: string) => void;
    showBackButton?: boolean;
    onModalClose: () => void;
  };
  Element: null;
}

export default class ShopifySyncModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare knowledgeHubService: KnowledgeHubService;

  @tracked selectedShop: ShopifyShop | null = null;
  @tracked syncBehavior: SyncBehaviorType = SyncBehaviorType.Sync;

  @use private shopsLoader = AsyncData<ShopifyShop[]>(async () => {
    let response = await get('/ember/import_service/import_sources/shopify/shops', {
      app_id: this.appService.app.id,
    });

    return response as ShopifyShop[];
  });

  get shops(): ShopifyShop[] {
    return this.shopsLoader.value ?? [];
  }

  get shopOptions() {
    return this.shops.map((shop: ShopifyShop) => {
      return {
        text: shop.shopify_domain,
        value: shop.shopify_domain,
      };
    });
  }

  get syncButtonDisabled() {
    return !this.selectedShop;
  }

  @action
  switchShop(value: string) {
    this.selectedShop =
      this.shops.find((shop: ShopifyShop) => shop.shopify_domain === value) || null;
  }

  @action
  async startSync() {
    if (!this.selectedShop) {
      return;
    }

    try {
      // Create import source with Shopify shop details
      let importSource = await this.knowledgeHubService.createImportSource({
        sourceType: SourceType.Shopify,
        syncBehavior: this.syncBehavior,
        shopifyDomain: this.selectedShop?.shopify_domain,
      });

      // Start the import
      await this.knowledgeHubService.startImport(importSource.id, this.syncBehavior);

      // Show success notification
      let notificationMessage =
        this.syncBehavior === SyncBehaviorType.Sync
          ? this.intl.t('knowledge-hub.sync-modal.shopify.sync-started')
          : this.intl.t('knowledge-hub.sync-modal.shopify.import-started');

      this.knowledgeHubService.notifySourceStateChangeConfirmation(
        notificationMessage,
        importSource.id,
      );

      // Track analytics event
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        context: 'import-content',
        object: 'import_content_shopify',
        section: 'knowledge-hub.new-content.shopify',
        place: 'knowledge-hub.new-content',
      });

      this.args.onModalClose();
    } catch (error) {
      // Handle error - notification will be shown by the service
      console.error('Failed to create Shopify import source:', error);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Shopify': typeof ShopifySyncModal;
    'knowledge-hub/setup-modal/sync-modal/shopify': typeof ShopifySyncModal;
  }
}
